import { useEffect } from 'react';

/**
 * Tell the canvas to re-measure itself after a side panel finishes animating.
 *
 * WHY THIS EXISTS (measured 2026-09-03, Arthi). `fitView` on <ReactFlow> is a MOUNT-ONLY prop, so
 * collapsing the AI panel changed the flex layout while ReactFlow kept the viewport width it had
 * measured on mount. With the chat closed, the graph still ended where the panel used to begin,
 * leaving a dead black band down the right-hand side of the editor.
 *
 * A `resize` event is the documented way to make ReactFlow recompute its bounds, and it is
 * deliberately NOT `fitView()`: this is a measurement bug, not a framing one. Re-framing would throw
 * away the user's zoom and pan on every toggle of the panel, which is a second bug traded for the
 * first.
 *
 * The delay must match the panel's CSS width transition (`.ai-panel`, 0.3s in
 * OntologyEditorPage.scss). Firing immediately measures the width the panel is animating AWAY from,
 * which reproduces the very band this fixes.
 *
 * @param dependency  Value whose change means "a panel just started animating" (e.g. `isOpen`).
 * @param delayMs     Must match the CSS transition duration.
 */
export function usePanelResizeNotifier(dependency: unknown, delayMs: number): void {
  useEffect(() => {
    const handle = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, delayMs);
    // Cleared on re-toggle and on unmount: without this, a fast double-toggle leaves an orphan timer
    // that fires against an unmounted tree.
    return () => window.clearTimeout(handle);
  }, [dependency, delayMs]);
}
